/**
 * Etiketten drucken (KONZEPT.md §5.3).
 *
 * Drei Wege, je nachdem, was gedruckt wird und wo:
 *
 * - **Labelmaker** (Brother P-touch, 12–24-mm-Band): fuer Lagerorte ein
 *   QR-Code mit Namen, fuer Geraete ein kleiner DataMatrix mit der
 *   Inventarnummer. Gezeichnet im Browser, also auch ohne Netz. Gedruckt
 *   direkt ueber USB oder als PDF fuer den PC mit Brother-Treiber.
 * - **DIN A4 gross**: ein Lagerort je Seite, zum Aufhaengen.
 * - **Etikettenbogen** 3 × 8 fuer den normalen Drucker.
 *
 * Der QR-Code eines Lagerorts enthaelt dessen Adresse. Wer ihn mit der
 * Kamera-App des Handys scannt, landet direkt in der WaWi.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import type { ArtikelMitZustand, LagerortKnoten } from "@thw/contracts";
import { stammplatzPfad } from "@thw/core-domain";
import { einstellungenSetzen, useEinstellungen } from "../api/einstellungen.js";
import { sitzungLesen } from "../api/klient.js";
import { lagerortBaum, useBestand } from "../lokal/bestand.js";
import { suchen } from "../lokal/suche.js";
import { bandPdf, bilderTeilen, dateiOeffnen, teilenMoeglich } from "../druck/ausgabe.js";
import {
  alsLeinwand,
  etikettZeichnen,
  laengeMm,
  lagerortQr,
  STAMMPLATZ_AB_MM,
  type Etikett,
} from "../druck/etikett.js";
import { bandAusStatus, druckauftrag, type Bandbreite, type Bild } from "../druck/ptouch.js";
import { DruckFehler, labelmakerVerbinden, webUsbVerfuegbar } from "../druck/webusb.js";
import {
  Eingabe,
  Karte,
  Knopf,
  Ladeanzeige,
  Nummer,
  Rueckmeldung,
  Schalter,
  Wahl,
} from "../ui/bausteine.jsx";

type Weg = "labelmaker" | "a4" | "bogen";

const BAND_WAHL: readonly { wert: "12" | "18" | "24"; text: string }[] = [
  { wert: "12", text: "12 mm" },
  { wert: "18", text: "18 mm" },
  { wert: "24", text: "24 mm" },
];

type Meldung = {
  art: "gut" | "warnung" | "still";
  text: string;
  zusatz?: string | undefined;
} | null;

function flach(
  knoten: readonly LagerortKnoten[],
  tiefe = 0,
): { k: LagerortKnoten; tiefe: number }[] {
  return knoten.flatMap((k) => [{ k, tiefe }, ...flach(k.kinder, tiefe + 1)]);
}

export function Etiketten() {
  const [parameter] = useSearchParams();
  const einstellung = useEinstellungen();
  const [lagerorte, setLagerorte] = useState<Set<string>>(
    () => new Set(parameter.getAll("lagerort")),
  );
  const [geraete, setGeraete] = useState<Map<string, ArtikelMitZustand>>(new Map());
  const [weg, setWeg] = useState<Weg>("labelmaker");
  const [meldung, setMeldung] = useState<Meldung>(null);
  const [laeuft, setLaeuft] = useState(false);

  // Lagerorte, Geraete und die oeffentliche Adresse aus der lokalen Kopie:
  // Der Labelmaker druckt auch an der Einsatzstelle ohne Netz (ADR 0009).
  const bestand = useBestand();
  const baum = useMemo(
    () => (bestand === undefined ? undefined : lagerortBaum(bestand)),
    [bestand],
  );

  // Aus der Artikelseite kommend: das Geraet gleich vorwaehlen.
  const artikelParameter = parameter.getAll("artikel").join(",");
  const vorgewaehlt = bestand !== undefined;
  useEffect(() => {
    if (artikelParameter === "" || bestand === undefined) return;
    const liste: ArtikelMitZustand[] = [];
    for (const id of artikelParameter.split(",")) {
      const artikel = bestand.artikel.get(id);
      const zustand = bestand.zustaende.get(id);
      if (artikel !== undefined && zustand !== undefined) liste.push({ artikel, zustand });
    }
    setGeraete(new Map(liste.map((a) => [a.artikel.id, a])));
    // Nur einmal, sobald der Bestand da ist -- nicht nach jedem Abgleich die
    // Auswahl zuruecksetzen.
  }, [artikelParameter, vorgewaehlt]);

  const alle = useMemo(() => flach(baum ?? []).filter(({ k }) => k.aktiv), [baum]);
  const basis = bestand?.basis_adresse ?? globalThis.location.origin;

  const mitStammplatz = einstellung.stammplatzDrucken;

  // Stabil, solange sich die Auswahl nicht aendert -- die Vorschau zeichnet
  // bei jeder neuen Liste alle Etiketten neu.
  const auswahl: { etikett: Etikett; name: string; hatStammplatz: boolean }[] = useMemo(() => {
    /**
     * Der Weg zum Stammplatz eines Artikels, leer ohne. Feste Orte haben
     * keinen -- sie sind kein Artikel.
     */
    const stammplatzVon = (artikel_id: string | null): string[] => {
      if (bestand === undefined || artikel_id === null) return [];
      const stammplatz = bestand.artikel.get(artikel_id)?.stammplatz_id ?? null;
      return stammplatz === null ? [] : stammplatzPfad(stammplatz, bestand.lagerorte);
    };
    return [
      ...alle
        .filter(({ k }) => lagerorte.has(k.id))
        .map(({ k }) => ({
          name: k.kennung,
          pfad: stammplatzVon(k.artikel_id),
          etikett: {
            art: "lagerort" as const,
            kennung: k.kennung,
            bezeichnung: k.bezeichnung,
            adresse: lagerortQr(basis, k),
          },
        })),
      ...[...geraete.values()].map((a) => ({
        name: a.artikel.inventarnummer,
        pfad: stammplatzVon(a.artikel.id),
        etikett: {
          art: "geraet" as const,
          inventarnummer: a.artikel.inventarnummer,
          bezeichnung: a.artikel.bezeichnung,
        },
      })),
    ].map(({ name, pfad, etikett }) => ({
      name,
      hatStammplatz: pfad.length > 0,
      etikett: { ...etikett, stammplatz: mitStammplatz ? pfad : undefined },
    }));
  }, [alle, lagerorte, geraete, basis, bestand, mitStammplatz]);
  const stammplatzWaehlbar = auswahl.some((a) => a.hatStammplatz);

  const band = einstellung.band;
  const nurGeraete = lagerorte.size === 0 && geraete.size > 0;

  if (baum === undefined) return <Ladeanzeige was="Die Lagerorte" />;

  /** DIN A4 und Bogen macht der Server -- dafuer braucht es Netz. */
  const serverPdf = async (format: "a4" | "bogen"): Promise<void> => {
    setLaeuft(true);
    setMeldung(null);
    try {
      const sitzung = sitzungLesen();
      const antwort = await fetch("/api/core/etiketten", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(sitzung === null ? {} : { authorization: `Bearer ${sitzung.token}` }),
        },
        body: JSON.stringify({ lagerort_ids: [...lagerorte], format, stammplatz: mitStammplatz }),
      });
      if (!antwort.ok) throw new Error(`Der Server antwortete mit ${String(antwort.status)}`);
      dateiOeffnen(await antwort.blob(), format === "a4" ? "lagerorte-a4.pdf" : "etiketten.pdf");
    } catch (f) {
      setMeldung({
        art: "warnung",
        text: "Das PDF ließ sich nicht erzeugen",
        zusatz: f instanceof Error ? f.message : String(f),
      });
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Etiketten drucken</h1>
      {meldung !== null && (
        <Rueckmeldung art={meldung.art} kopf={meldung.text} zusatz={meldung.zusatz} />
      )}

      <Karte titel="1. Was">
        <LagerortAuswahl alle={alle} gewaehlt={lagerorte} aendern={setLagerorte} />
        <GeraeteAuswahl gewaehlt={geraete} aendern={setGeraete} />
      </Karte>

      <Karte titel="2. Wie">
        <Wahl
          beschriftung="Drucken auf"
          wert={nurGeraete ? "labelmaker" : weg}
          moeglichkeiten={[
            { wert: "labelmaker", text: "Labelmaker" },
            { wert: "a4", text: "DIN A4 groß" },
            { wert: "bogen", text: "Etikettenbogen" },
          ]}
          aendern={(neu) => {
            setWeg(nurGeraete ? "labelmaker" : neu);
          }}
        />
        {/* Nur wenn etwas in der Auswahl einen Stammplatz hat (ADR 0017). */}
        {stammplatzWaehlbar && (
          <Schalter
            beschriftung="Stammplatz mitdrucken"
            an={mitStammplatz}
            aendern={(an) => {
              einstellungenSetzen({ stammplatzDrucken: an });
            }}
            hinweis={
              weg === "labelmaker" || nurGeraete
                ? `Bei Packstücken und Geräten, auf Band ab ${String(STAMMPLATZ_AB_MM)} mm.`
                : "Bei Packstücken, statt des Orts, in dem sie gerade stehen."
            }
          />
        )}
        {weg !== "labelmaker" && !nurGeraete && (
          <>
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              {weg === "a4"
                ? "Ein Lagerort je Seite, QR-Code 16 cm groß — zum Aufhängen an Halle, Regal oder Fahrzeug."
                : "24 Etiketten je Blatt, für selbstklebende Bögen 70 × 35 mm."}{" "}
              Nur für Lagerorte; Geräte gehen über den Labelmaker.
            </p>
            <Knopf
              art="haupt"
              disabled={lagerorte.size === 0 || laeuft}
              onClick={() => void serverPdf(weg)}
            >
              {laeuft ? "wird erzeugt …" : `PDF für ${String(lagerorte.size)} Lagerorte`}
            </Knopf>
          </>
        )}
      </Karte>

      {(weg === "labelmaker" || nurGeraete) && (
        <Labelmaker auswahl={auswahl} band={band} melden={setMeldung} />
      )}
    </div>
  );
}

function LagerortAuswahl({
  alle,
  gewaehlt,
  aendern,
}: {
  alle: readonly { k: LagerortKnoten; tiefe: number }[];
  gewaehlt: Set<string>;
  aendern: (neu: Set<string>) => void;
}) {
  const [offen, setOffen] = useState(gewaehlt.size > 0);
  return (
    <details
      open={offen}
      onToggle={(e) => {
        setOffen(e.currentTarget.open);
      }}
    >
      <summary className="flex min-h-12 cursor-pointer items-center justify-between font-semibold">
        <span>Lagerorte</span>
        <span className="text-sm font-normal text-neutral-500">{gewaehlt.size} gewählt</span>
      </summary>
      <div className="mb-2 flex gap-2">
        <Knopf
          onClick={() => {
            aendern(new Set(alle.map(({ k }) => k.id)));
          }}
        >
          Alle
        </Knopf>
        <Knopf
          onClick={() => {
            aendern(new Set());
          }}
        >
          Keine
        </Knopf>
      </div>
      <ul className="flex flex-col gap-1">
        {alle.map(({ k, tiefe }) => (
          <li key={k.id}>
            <label
              className="flex min-h-12 items-center gap-3 rounded-xl bg-neutral-100 px-3 dark:bg-neutral-700"
              style={{ marginLeft: `${String(tiefe * 12)}px` }}
            >
              <input
                type="checkbox"
                className="size-6"
                checked={gewaehlt.has(k.id)}
                onChange={(e) => {
                  const neu = new Set(gewaehlt);
                  if (e.target.checked) neu.add(k.id);
                  else neu.delete(k.id);
                  aendern(neu);
                }}
              />
              <span>
                <span className="font-semibold">{k.kennung}</span> — {k.bezeichnung}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </details>
  );
}

function GeraeteAuswahl({
  gewaehlt,
  aendern,
}: {
  gewaehlt: Map<string, ArtikelMitZustand>;
  aendern: (neu: Map<string, ArtikelMitZustand>) => void;
}) {
  const [suche, setSuche] = useState("");
  const gesucht = suche.trim();
  const bestand = useBestand();
  const data = useMemo(
    () =>
      bestand === undefined || gesucht.length < 2
        ? undefined
        : {
            artikel: suchen(bestand.artikel.values(), bestand.zustaende, gesucht, { limit: 10 })
              .treffer,
          },
    [bestand, gesucht],
  );

  return (
    <details open={gewaehlt.size > 0 || undefined}>
      <summary className="flex min-h-12 cursor-pointer items-center justify-between font-semibold">
        <span>Geräte (DataMatrix mit Inventarnummer)</span>
        <span className="text-sm font-normal text-neutral-500">{gewaehlt.size} gewählt</span>
      </summary>
      {gewaehlt.size > 0 && (
        <ul className="mb-2 flex flex-col gap-1">
          {[...gewaehlt.values()].map((a) => (
            <li
              key={a.artikel.id}
              className="flex min-h-12 items-center justify-between gap-2 rounded-xl bg-neutral-100 px-3 dark:bg-neutral-700"
            >
              <span>
                <Nummer>{a.artikel.inventarnummer}</Nummer> {a.artikel.bezeichnung}
              </span>
              <button
                type="button"
                className="size-12 text-xl"
                aria-label={`${a.artikel.inventarnummer} entfernen`}
                onClick={() => {
                  const neu = new Map(gewaehlt);
                  neu.delete(a.artikel.id);
                  aendern(neu);
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <Eingabe
        beschriftung="Gerät hinzufügen"
        wert={suche}
        aendern={setSuche}
        hinweis="Nummer (auch die letzten Ziffern) oder Bezeichnung."
      />
      <ul className="mt-1 flex flex-col gap-1">
        {(data?.artikel ?? [])
          .filter((a) => !gewaehlt.has(a.artikel.id))
          .map((a) => (
            <li key={a.artikel.id}>
              <button
                type="button"
                className="flex min-h-12 w-full items-center justify-between gap-2 rounded-xl bg-white px-3 text-left active:bg-neutral-100 dark:bg-neutral-800"
                onClick={() => {
                  aendern(new Map(gewaehlt).set(a.artikel.id, a));
                  setSuche("");
                }}
              >
                <span>
                  <Nummer>{a.artikel.inventarnummer}</Nummer> {a.artikel.bezeichnung}
                </span>
                <span className="text-xl">+</span>
              </button>
            </li>
          ))}
      </ul>
    </details>
  );
}

/**
 * Vorschau und Druck fuer den Labelmaker.
 *
 * Die Vorschau zeigt genau die Punkte, die der Drucker bekommt -- was hier
 * falsch aussieht, sieht auch auf dem Band falsch aus.
 */
function Labelmaker({
  auswahl,
  band,
  melden,
}: {
  auswahl: readonly { etikett: Etikett; name: string }[];
  band: Bandbreite;
  melden: (m: Meldung) => void;
}) {
  const { anordnung } = useEinstellungen();
  const [bilder, setBilder] = useState<Bild[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const mitLagerort = auswahl.some((a) => a.etikett.art === "lagerort");
  const mitStammplatz = auswahl.some((a) => (a.etikett.stammplatz?.length ?? 0) > 0);

  useEffect(() => {
    let gueltig = true;
    void Promise.all(auswahl.map((a) => etikettZeichnen(a.etikett, band, anordnung))).then(
      (neu) => {
        if (gueltig) setBilder(neu);
      },
    );
    return () => {
      gueltig = false;
    };
  }, [auswahl, band, anordnung]);

  /**
   * Direkt ueber USB. Der Drucker sagt, welches Band drin ist -- passt es
   * nicht zur Auswahl, wird fuer das eingelegte Band neu gezeichnet, statt
   * einen Fehldruck zu riskieren.
   */
  const usbDrucken = async (): Promise<void> => {
    setLaeuft(true);
    melden({ art: "still", text: "Verbinde mit dem Labelmaker …" });
    let geraet;
    try {
      geraet = await labelmakerVerbinden(true);
      const status = await geraet.status();
      if (status.fehler.length > 0) throw new DruckFehler(status.fehler.join(", "));
      const eingelegt = bandAusStatus(status.bandMm);
      if (eingelegt === null) {
        throw new DruckFehler(`Dieses Band (${String(status.bandMm)} mm) wird nicht unterstützt`);
      }
      let zuDrucken = bilder;
      if (eingelegt !== band) {
        einstellungenSetzen({ band: eingelegt });
        zuDrucken = await Promise.all(
          auswahl.map((a) => etikettZeichnen(a.etikett, eingelegt, anordnung)),
        );
      }
      melden({ art: "still", text: `Drucke ${String(zuDrucken.length)} Etiketten …` });
      await geraet.drucken(druckauftrag(zuDrucken, eingelegt), zuDrucken.length);
      melden({
        art: "gut",
        text: `✓ ${String(zuDrucken.length)} ${zuDrucken.length === 1 ? "Etikett" : "Etiketten"} gedruckt`,
        zusatz:
          eingelegt !== band
            ? `Eingelegt war ${String(eingelegt)}-mm-Band — dafür neu gesetzt.`
            : undefined,
      });
    } catch (f) {
      melden({
        art: "warnung",
        text: "Nicht gedruckt",
        zusatz: f instanceof Error ? f.message : String(f),
      });
    } finally {
      await geraet?.schliessen().catch(() => undefined);
      setLaeuft(false);
    }
  };

  const gesamtMm = bilder.reduce((s, b) => s + laengeMm(b), 0);

  return (
    <Karte titel="3. Labelmaker">
      <Wahl
        beschriftung="Bandbreite"
        wert={String(band) as "12" | "18" | "24"}
        moeglichkeiten={BAND_WAHL}
        aendern={(neu) => {
          einstellungenSetzen({ band: Number(neu) as Bandbreite });
        }}
      />
      {mitLagerort && band >= 18 && (
        <Wahl
          beschriftung="Name des Lagerorts"
          wert={anordnung}
          moeglichkeiten={[
            { wert: "darunter", text: "Unter dem QR-Code" },
            { wert: "daneben", text: "Neben dem QR-Code" },
          ]}
          aendern={(neu) => {
            einstellungenSetzen({ anordnung: neu });
          }}
        />
      )}
      {mitLagerort && band < 18 && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Auf {band}-mm-Band ist unter dem QR-Code kein Platz — der Name steht daneben.
        </p>
      )}
      {mitStammplatz && band < STAMMPLATZ_AB_MM && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Auf {band}-mm-Band ist für den Stammplatz kein Platz — er steht erst ab {STAMMPLATZ_AB_MM}{" "}
          mm auf dem Etikett.
        </p>
      )}
      {mitStammplatz && band >= STAMMPLATZ_AB_MM && mitLagerort && anordnung === "darunter" && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Etiketten mit Stammplatz haben den Text neben dem QR-Code.
        </p>
      )}

      {auswahl.length === 0 ? (
        <p className="text-neutral-500">Oben Lagerorte oder Geräte wählen.</p>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {bilder.map((b, i) => (
              <li key={i} className="flex flex-col gap-1">
                <Vorschau bild={b} band={band} />
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  {auswahl[i]?.name} · etwa {laengeMm(b)} mm
                </span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            {bilder.length} {bilder.length === 1 ? "Etikett" : "Etiketten"}, zusammen etwa{" "}
            {gesamtMm} mm Band.
          </p>

          <div className="flex flex-wrap gap-2">
            {webUsbVerfuegbar() && (
              <Knopf
                art="haupt"
                disabled={laeuft || bilder.length === 0}
                onClick={() => void usbDrucken()}
              >
                {laeuft ? "druckt …" : "Drucken über USB"}
              </Knopf>
            )}
            <Knopf
              disabled={bilder.length === 0}
              onClick={() => {
                void bandPdf(bilder, band).then((pdf) => {
                  dateiOeffnen(pdf, `etiketten-${String(band)}mm.pdf`);
                });
              }}
            >
              Als PDF
            </Knopf>
            {teilenMoeglich() && (
              <Knopf
                disabled={bilder.length === 0}
                onClick={() => {
                  void bilderTeilen(
                    bilder,
                    auswahl.map((a) => a.name),
                  ).catch(() => undefined);
                }}
              >
                Teilen
              </Knopf>
            )}
          </div>
          <details className="text-sm text-neutral-600 dark:text-neutral-300">
            <summary className="flex min-h-12 cursor-pointer items-center font-semibold">
              Welcher Weg wann?
            </summary>
            <ul className="flex list-disc flex-col gap-1 pl-5">
              <li>
                <strong>Über USB</strong>: Labelmaker per Kabel an Handy (USB-OTG) oder Rechner, in
                Chrome oder Edge. Geht ohne Netz und ohne Brother-App.
              </li>
              <li>
                <strong>Als PDF</strong>: am PC mit installiertem Brother-Treiber öffnen, drucken,
                den Labelmaker wählen und als Papier das Band ({band} mm) einstellen. Auf einem PC
                mit Treiber ist das der Weg — der Treiber belegt dort den USB-Anschluss.
              </li>
              <li>
                <strong>Teilen</strong>: gibt die Etiketten als Bild an eine andere App weiter.
              </li>
            </ul>
          </details>
        </>
      )}
    </Karte>
  );
}

/** Ein Etikett, wie es auf dem Band aussieht: weisses Band, schwarzer Druck. */
function Vorschau({ bild, band }: { bild: Bild; band: Bandbreite }) {
  const url = useMemo(() => alsLeinwand(bild).toDataURL("image/png"), [bild]);
  // Auf dem Bildschirm etwa 3 px je mm -- gross genug, um den Code zu erkennen.
  const hoehePx = band * 3.2;
  return (
    <div
      className="flex items-center overflow-x-auto rounded-md bg-white px-2 shadow-sm ring-1 ring-neutral-300"
      style={{ height: `${String(hoehePx)}px` }}
    >
      <img
        src={url}
        alt="Vorschau des Etiketts"
        style={{
          height: `${String(hoehePx * (bild.hoehe / (band * 7.087)))}px`,
          imageRendering: "pixelated",
        }}
      />
    </div>
  );
}
